import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

// Select messages, edit the auto-generated report (directly or via Mo edit instructions), download
// it as a PDF. Checkboxes are injected here so streamed messages don't carry selection markup.
// Generation is async: the preview arrives over Turbo Stream into a token-named div, so a
// superseded generate finds no target.
const CHECKBOX_MARKER = "massimo-pdf-checkbox";
// Look lives in application.tailwind.css (.massimo-pdf-checkbox); only positioning is set here.
const CHECKBOX_CLASS = `${CHECKBOX_MARKER} absolute -left-6 top-0.5`;
const SELECTED_CLASS = "massimo-pdf-selected";
const MAX_SELECTED = 60;
const THUMBNAIL_WIDTH = 640;
// Print size for a full-width chart; the export also rejects an image over 10 million pixels.
const MAX_EXPORT_WIDTH = 1200;
const MAX_EXPORT_HEIGHT = 800;

export default class extends Controller {
  static targets = [
    "bar", "count", "create", "selectAll", "messages", "error",
    "modal", "instruction", "editControls", "generateLabel", "status", "preview", "previewContent", "modalError",
    "download", "downloadContainer", "downloaded",
  ];
  static values = { generateUrl: String, downloadUrl: String };

  disconnect() {
    this.exit();
  }

  start() {
    if (this.selecting || !this.hasBarTarget) return;
    this.selecting = true;
    this.barTarget.hidden = false;
    this.messagesTarget.classList.add("massimo-pdf-selecting");
    this.selectables.forEach((el) => this.addCheckbox(el));
    this.refresh();
  }

  cancel() {
    this.exit();
  }

  // Also runs on disconnect (frame swap), by which point the targets may be gone.
  exit() {
    if (!this.selecting) return;
    this.selecting = false;
    this.dirty = false;
    this.token = null;
    // Orphans anything still in flight, so a late generate or download can't touch the next session.
    this.session = (this.session ?? 0) + 1;
    this.closeBuilder();
    this.resetBuilder();
    if (this.hasBarTarget) this.barTarget.hidden = true;
    this.clearError();
    if (!this.hasMessagesTarget) return;

    this.messagesTarget.classList.remove("massimo-pdf-selecting");
    this.messagesTarget.querySelectorAll(`.${CHECKBOX_MARKER}`).forEach((box) => box.remove());
    this.selectables.forEach((el) => el.classList.remove(SELECTED_CLASS));
  }

  // New messages can stream in while the bar is open.
  refresh() {
    if (!this.selecting) return;
    this.selectables.forEach((el) => this.addCheckbox(el));
    this.updateCount();
  }

  toggle(event) {
    const message = event.target.closest("[data-pdf-selectable]");
    if (message) message.classList.toggle(SELECTED_CLASS, event.target.checked);
    this.updateCount();
  }

  // --- builder modal ---------------------------------------------------------

  openBuilder() {
    if (this.selectedMessages().length === 0) return;
    this.modalTarget.showPopover();
    // First open generates right away; reopening keeps the existing draft, Regenerate replaces it.
    if (!this.token) this.generate();
  }

  closeBuilder() {
    if (this.hasModalTarget && this.modalTarget.matches(":popover-open")) this.modalTarget.hidePopover();
  }

  resetBuilder() {
    this.clearDownloaded();
    this.clearModalError();
    if (this.hasPreviewTarget) this.previewTarget.innerHTML = "";
    if (this.hasInstructionTarget) this.instructionTarget.value = "";
    if (this.hasStatusTarget) this.statusTarget.hidden = true;
    if (this.hasEditControlsTarget) this.editControlsTarget.hidden = true;
    if (this.hasGenerateLabelTarget) this.generateLabelTarget.textContent = "Regenerate";
    if (this.hasDownloadContainerTarget) this.setDownloadHidden(true);
  }

  // Hides the wrapper, not the button: it has no display utility to fight the [hidden] rule.
  setDownloadHidden(hidden) {
    this.downloadContainerTarget.hidden = hidden;
    this.downloadTarget.disabled = hidden;
  }

  stale(session) {
    return session !== this.session;
  }

  async generate() {
    const selected = this.selectedMessages();
    if (selected.length === 0) return;
    if (this.dirty && !window.confirm("Regenerating replaces the edits you've made. Continue?")) return;

    this.token = crypto.randomUUID();
    this.clearDownloaded();
    this.previewTarget.innerHTML = `<div id="massimo-report-preview-${this.token}"></div>`;
    // While Mo writes, only the spinner (and Done) shows: no Regenerate or Download to dead-click.
    this.setDownloadHidden(true);
    this.editControlsTarget.hidden = true;
    this.clearModalError();
    this.statusTarget.hidden = false;
    const session = this.session;
    try {
      const response = await post(this.generateUrlValue, {
        body: {
          message_eids: selected.map((el) => el.dataset.pdfMessageEid),
          instruction: this.instructionTarget.value,
          token: this.token,
        },
      });
      if (!response.ok) throw new Error(`Report generation failed (${response.statusCode})`);
    } catch (error) {
      console.error(error);
      if (this.stale(session)) return;
      this.statusTarget.hidden = true;
      // The preview was already wiped for this attempt, so there's no draft to "regenerate".
      this.generateLabelTarget.textContent = "Try again";
      this.editControlsTarget.hidden = false;
      this.showModalError("Couldn't start that report. Try again.");
    }
  }

  previewContentTargetConnected(element) {
    if (element.dataset.token !== this.token) {
      element.remove();
      return;
    }
    this.statusTarget.hidden = true;
    this.dirty = false;
    this.clearDownloaded();
    const failed = !!element.dataset.error;
    this.generateLabelTarget.textContent = failed ? "Try again" : "Regenerate";
    this.editControlsTarget.hidden = false;
    this.setDownloadHidden(failed);
    this.renderThumbnails();
  }

  draftChanged() {
    this.dirty = true;
    this.clearDownloaded();
  }

  async download() {
    if (!this.hasPreviewContentTarget) return;

    this.clearModalError();
    this.clearDownloaded();
    this.downloadTarget.disabled = true;
    const session = this.session;
    try {
      const report = this.serializeReport();
      const response = await post(this.downloadUrlValue, {
        body: {
          report,
          branding: this.previewContentTarget.querySelector('[data-report-field="branding"]')?.checked ?? true,
          chart_images: this.captureCharts(report.sections.map((section) => section.chart_id).filter(Boolean)),
        },
      });

      if (!response.ok) throw new Error(`Report download failed (${response.statusCode})`);
      // Saved even for a closed session: the PDF was explicitly asked for and is already built.
      this.save(await response.response.blob(), response.response.headers.get("Content-Disposition"));
      if (this.stale(session)) return;
      this.downloadedTarget.textContent = "PDF downloaded. You can keep editing and download it again.";
    } catch (error) {
      console.error(error);
      if (this.stale(session)) return;
      this.showModalError("Couldn't build that PDF. Try again.");
    } finally {
      if (!this.stale(session)) this.downloadTarget.disabled = false;
    }
  }

  removeSection(event) {
    this.draftChanged();
    event.target.closest("[data-report-section]")?.remove();
    if (!this.hasPreviewContentTarget) return;
    if (this.previewContentTarget.querySelectorAll("[data-report-section]").length === 0) {
      // Report.parse requires at least one section.
      this.previewContentTarget.querySelector("[data-report-empty]")?.removeAttribute("hidden");
      this.downloadTarget.disabled = true;
    }
  }

  serializeReport() {
    const root = this.previewContentTarget;
    const field = (scope, name) => scope.querySelector(`[data-report-section-field="${name}"]`)?.value ?? null;
    return {
      title: root.querySelector('[data-report-field="title"]')?.value ?? "",
      sections: Array.from(root.querySelectorAll("[data-report-section]")).map((section) => ({
        heading: field(section, "heading"),
        body_markdown: field(section, "body_markdown"),
        chart_id: section.dataset.chartId || null,
        chart_explanation: field(section, "chart_explanation"),
      })),
    };
  }

  // --- selection -----------------------------------------------------------

  get selectables() {
    if (!this.hasMessagesTarget) return [];
    return Array.from(this.messagesTarget.querySelectorAll("[data-pdf-selectable]"));
  }

  // Scoped to the injected checkbox: answered question-set inputs are checked too.
  selectedMessages() {
    return this.selectables.filter((el) => this.selected(el));
  }

  selected(element) {
    return !!element.querySelector(`input.${CHECKBOX_MARKER}:checked`);
  }

  setSelected(element, checked) {
    const box = element.querySelector(`input.${CHECKBOX_MARKER}`);
    if (!box) return;
    box.checked = checked;
    element.classList.toggle(SELECTED_CLASS, checked);
  }

  // Mo's responses carry the report's substance; bulk selection targets them and caps at the limit.
  bulkSelectables() {
    return this.selectables.filter((el) => el.hasAttribute("data-pdf-assistant")).slice(0, MAX_SELECTED);
  }

  allBulkSelected() {
    const bulk = this.bulkSelectables();
    return bulk.length > 0 && bulk.every((el) => this.selected(el));
  }

  // Selecting replaces the current selection, so a stray manual pick can't push past the limit.
  toggleSelectAll() {
    const clearing = this.allBulkSelected();
    this.selectables.forEach((el) => this.setSelected(el, false));
    if (!clearing) this.bulkSelectables().forEach((el) => this.setSelected(el, true));
    this.updateCount();
  }

  addCheckbox(element) {
    if (element.querySelector(`.${CHECKBOX_MARKER}`)) return;

    element.classList.add("relative");
    const box = document.createElement("input");
    box.type = "checkbox";
    box.className = CHECKBOX_CLASS;
    box.setAttribute("aria-label", "Include this message in the report");
    box.dataset.action = "change->admin--massimo--report-builder#toggle";
    element.prepend(box);
  }

  updateCount() {
    const count = this.selectedMessages().length;
    const overLimit = count > MAX_SELECTED;
    this.countTarget.textContent = count === 1 ? "1 selected" : `${count} selected`;
    this.createTarget.disabled = count === 0 || overLimit;
    this.updateSelectAll();
    if (overLimit) this.showError(`Pick at most ${MAX_SELECTED} messages.`);
    else this.clearError();
  }

  updateSelectAll() {
    if (!this.hasSelectAllTarget) return;
    const assistantCount = this.selectables.filter((el) => el.hasAttribute("data-pdf-assistant")).length;
    this.selectAllTarget.hidden = assistantCount === 0;
    if (assistantCount === 0) return;

    if (this.allBulkSelected()) {
      this.selectAllTarget.textContent = "Clear selection";
    } else if (assistantCount > MAX_SELECTED) {
      this.selectAllTarget.textContent = `Select first ${MAX_SELECTED}`;
    } else {
      this.selectAllTarget.textContent = "Select all";
    }
  }

  // --- charts --------------------------------------------------------------

  // On-screen canvases, so the PDF matches the panel and survives an evicted query payload.
  captureCharts(chartIds) {
    const images = {};

    chartIds.forEach((chartId) => {
      const canvas = this.chartCanvas(chartId);
      if (!canvas) return;
      try {
        images[chartId] = this.resizeForExport(canvas).toDataURL("image/png");
      } catch (error) {
        console.error("Could not capture chart for the report", error);
      }
    });
    return images;
  }

  // A retina canvas can carry far more pixels than the PDF can use, and the export drops what it
  // can't fit, so shrink here rather than posting megabytes that come back as an unavailable chart.
  resizeForExport(source) {
    const scale = Math.min(1, MAX_EXPORT_WIDTH / source.width, MAX_EXPORT_HEIGHT / source.height);
    if (scale === 1) return source;

    const output = document.createElement("canvas");
    output.width = Math.round(source.width * scale);
    output.height = Math.round(source.height * scale);
    output.getContext("2d").drawImage(source, 0, 0, output.width, output.height);
    return output;
  }

  // Same lookup captureCharts uses, so a section without a thumbnail is one whose chart the PDF will miss.
  renderThumbnails() {
    this.previewContentTarget.querySelectorAll("[data-report-section][data-chart-id]").forEach((section) => {
      const container = section.querySelector("[data-report-chart]");
      const source = this.chartCanvas(section.dataset.chartId);
      if (!container || !source) return;

      const thumbnail = document.createElement("canvas");
      thumbnail.width = THUMBNAIL_WIDTH;
      thumbnail.height = Math.round(THUMBNAIL_WIDTH * (source.height / source.width));
      thumbnail.className = "w-full rounded border border-gray-200 bg-white";
      thumbnail.getContext("2d").drawImage(source, 0, 0, thumbnail.width, thumbnail.height);
      container.appendChild(thumbnail);
    });
  }

  chartCanvas(chartId) {
    if (!this.hasMessagesTarget) return null;
    const canvas = this.messagesTarget.querySelector(`[data-massimo-chart-id="${chartId}"] canvas`);
    return canvas && canvas.width && canvas.height ? canvas : null;
  }

  // --- output --------------------------------------------------------------

  save(blob, contentDisposition) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = this.filenameFrom(contentDisposition);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  filenameFrom(contentDisposition) {
    const match = /filename="?([^";]+)"?/.exec(contentDisposition || "");
    return match ? match[1] : "mo-report.pdf";
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  clearError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  showModalError(message) {
    this.modalErrorTarget.textContent = message;
    this.modalErrorTarget.hidden = false;
  }

  clearModalError() {
    if (!this.hasModalErrorTarget) return;
    this.modalErrorTarget.textContent = "";
    this.modalErrorTarget.hidden = true;
  }

  clearDownloaded() {
    if (this.hasDownloadedTarget) this.downloadedTarget.textContent = "";
  }
}
